// Local-tool ("CLI") login: a local tool (e.g. an MCP server) opens this page pointed at a localhost
// callback, the user signs in normally, and we hand the resulting token back to that callback.
// Mirrors the OAuth 2.0 "loopback" flow used by gh/gcloud.

/** Where the pending request is stashed so it survives the /login navigation. */
export const CLI_AUTH_STORAGE_KEY = "cli_auth_request"

/**
 * A pending request is only honored for a short window. This bounds the "abandoned request" case:
 * if the user starts the CLI flow, drops it, then logs in normally later in the same tab, an expired
 * request must not silently replay the token handoff to its loopback callback.
 */
export const CLI_AUTH_REQUEST_TTL_MS = 5 * 60 * 1000

export interface CliAuthRequest {
	redirectUri: string
	state: string
}

/** Shape actually persisted: the public request plus the stamp used for the TTL check. */
interface StoredCliAuthRequest extends CliAuthRequest {
	createdAt: number
}

const isStoredCliAuthRequest = (value: unknown): value is StoredCliAuthRequest =>
	typeof value === "object" &&
	value !== null &&
	"redirectUri" in value &&
	typeof value.redirectUri === "string" &&
	"state" in value &&
	typeof value.state === "string" &&
	"createdAt" in value &&
	typeof value.createdAt === "number"

/**
 * SECURITY: the redirect target must be a loopback address. Without this check the page would be an
 * open redirect that any site could use to exfiltrate a valid session token via
 * `?redirect_uri=https://evil.example`. Only http(s) loopback hosts are ever accepted.
 */
export function isLoopbackRedirectUri(uri: string): boolean {
	let url: URL
	try {
		url = new URL(uri)
	} catch {
		return false
	}

	if (url.protocol !== "http:" && url.protocol !== "https:") return false

	// url.hostname keeps IPv6 brackets, so "[::1]" arrives verbatim as "[::1]".
	const loopbackHosts = new Set(["127.0.0.1", "localhost", "::1", "[::1]"])
	return loopbackHosts.has(url.hostname)
}

export function clearCliAuthRequest(): void {
	sessionStorage.removeItem(CLI_AUTH_STORAGE_KEY)
}

export function readCliAuthRequest(): CliAuthRequest | null {
	let parsed: unknown
	try {
		const raw = sessionStorage.getItem(CLI_AUTH_STORAGE_KEY)
		if (!raw) return null
		parsed = JSON.parse(raw)
	} catch {
		// Corrupt JSON: purge it too, so a bad value cannot cause a persistent read error.
		clearCliAuthRequest()
		return null
	}

	if (isStoredCliAuthRequest(parsed)) {
		const { redirectUri, state, createdAt } = parsed
		if (Date.now() - createdAt <= CLI_AUTH_REQUEST_TTL_MS && isLoopbackRedirectUri(redirectUri)) {
			return { redirectUri, state }
		}
	}

	// Stale, malformed, or non-loopback: drop it so it can never replay on a later login.
	clearCliAuthRequest()
	return null
}

export function storeCliAuthRequest(request: CliAuthRequest): void {
	const stored: StoredCliAuthRequest = { ...request, createdAt: Date.now() }
	sessionStorage.setItem(CLI_AUTH_STORAGE_KEY, JSON.stringify(stored))
}
